import { type ReactElement, cloneElement, useId, useMemo, useState } from 'react'
import {
  type CargoKind,
  QUOTE_STATE_LABEL,
  type QuoteDraft,
  type QuoteState,
  nextQuoteState,
  quoteBlockers,
  quoteId,
  quoteTotals,
} from '../lib/rate-desk'
import { CustomerQuotes } from './CustomerQuotes'
import { MarketIntelligence } from './MarketIntelligence'
import { RateBook } from './RateBook'

type View = 'today' | 'customers' | 'rates' | 'market'

const mono = "var(--mono, var(--font-mono, ui-monospace, 'SF Mono', Menlo, monospace))"

const SAMPLE: QuoteDraft[] = [
  {
    id: 'FR-261005-A7K',
    customer: 'Northstar Projects',
    origin: 'CNSHA',
    destination: 'NLRTM',
    equipment: '40HC',
    cargoKind: 'standard',
    readyDate: '2026-10-12',
    weightKg: 12400,
    lengthCm: null,
    widthCm: null,
    heightCm: null,
    centreOfGravityKnown: false,
    buyAmount: 3180,
    marginPct: 18,
    state: 'pricing',
    createdAt: '2026-10-05T08:14:00Z',
  },
  {
    id: 'FR-261004-P2M',
    customer: 'Aster Machinery',
    origin: 'INMUN',
    destination: 'DEHAM',
    equipment: "40' FR",
    cargoKind: 'odc',
    readyDate: '2026-10-19',
    weightKg: 28600,
    lengthCm: 890,
    widthCm: 318,
    heightCm: 365,
    centreOfGravityKnown: false,
    buyAmount: 12840,
    marginPct: 17,
    state: 'needs_data',
    createdAt: '2026-10-04T15:30:00Z',
  },
  {
    id: 'FR-261004-J9Q',
    customer: 'Hearth & Field',
    origin: 'VNSGN',
    destination: 'GBFXT',
    equipment: '40HC',
    cargoKind: 'standard',
    readyDate: '2026-10-15',
    weightKg: 9800,
    lengthCm: null,
    widthCm: null,
    heightCm: null,
    centreOfGravityKnown: false,
    buyAmount: 3050,
    marginPct: 9,
    state: 'approval',
    createdAt: '2026-10-04T11:03:00Z',
  },
  {
    id: 'FR-261003-R4B',
    customer: 'Morrow Living',
    origin: 'CNNGB',
    destination: 'USNYC',
    equipment: '40HC',
    cargoKind: 'standard',
    readyDate: '2026-10-21',
    weightKg: 10500,
    lengthCm: null,
    widthCm: null,
    heightCm: null,
    centreOfGravityKnown: false,
    buyAmount: 4150,
    marginPct: 21,
    state: 'sent',
    createdAt: '2026-10-03T09:48:00Z',
  },
]

const stateTone: Record<QuoteState, { fg: string; bg: string }> = {
  needs_data: { fg: '#b45309', bg: '#f59e0b18' },
  pricing: { fg: '#2563eb', bg: '#3b82f618' },
  approval: { fg: '#be123c', bg: '#e11d4818' },
  sent: { fg: '#047857', bg: '#10b98118' },
}

function loadDrafts(): QuoteDraft[] {
  if (typeof window === 'undefined') return SAMPLE
  try {
    const saved = window.localStorage.getItem('frm-rate-desk-v1')
    return saved ? (JSON.parse(saved) as QuoteDraft[]) : SAMPLE
  } catch {
    return SAMPLE
  }
}

function Field({ label, children }: { label: string; children: ReactElement<{ id?: string }> }) {
  const id = useId()
  return (
    <div className="frd-field">
      <label htmlFor={id}>{label}</label>
      {cloneElement(children, { id })}
    </div>
  )
}

export function RateDesk() {
  const [view, setView] = useState<View>('customers')
  const [drafts, setDrafts] = useState<QuoteDraft[]>(loadDrafts)
  const [selected, setSelected] = useState<string>(drafts[0]?.id ?? '')
  const [composer, setComposer] = useState(false)
  const active = drafts.find((q) => q.id === selected) ?? drafts[0]
  const counts = useMemo(() => {
    const result: Record<QuoteState, number> = { needs_data: 0, pricing: 0, approval: 0, sent: 0 }
    for (const quote of drafts) result[quote.state]++
    return result
  }, [drafts])

  const save = (next: QuoteDraft[]) => {
    setDrafts(next)
    try {
      window.localStorage.setItem('frm-rate-desk-v1', JSON.stringify(next))
    } catch {
      /* private mode */
    }
  }

  const update = (id: string, change: Partial<QuoteDraft>) =>
    save(drafts.map((q) => (q.id === id ? { ...q, ...change } : q)))

  return (
    <div className="frd">
      <style>{styles}</style>
      <header className="frd-head">
        <div>
          <div className="frd-kicker">
            <span /> Freight rates · the ledger
          </div>
          <h1>Everything needed to quote well.</h1>
          <p>Clear costs, honest margins, and no operational surprises.</p>
        </div>
        <button type="button" className="frd-primary" onClick={() => setComposer(true)}>
          ＋ New quote
        </button>
      </header>

      <nav className="frd-tabs" aria-label="Freight rates sections">
        <button
          type="button"
          className={view === 'customers' ? 'active' : ''}
          onClick={() => setView('customers')}
        >
          Client desk <b>3</b>
        </button>
        <button
          type="button"
          className={view === 'today' ? 'active' : ''}
          onClick={() => setView('today')}
        >
          Today <b>{drafts.filter((d) => d.state !== 'sent').length}</b>
        </button>
        <button
          type="button"
          className={view === 'rates' ? 'active' : ''}
          onClick={() => setView('rates')}
        >
          My rates <b>6</b>
        </button>
        <button
          type="button"
          className={view === 'market' ? 'active' : ''}
          onClick={() => setView('market')}
        >
          Market rates
        </button>
      </nav>

      {view === 'market' ? (
        <MarketIntelligence />
      ) : view === 'rates' ? (
        <RateBook />
      ) : view === 'customers' ? (
        <CustomerQuotes />
      ) : (
        <main className="frd-body">
          <section className="frd-states" aria-label="Quote work queue">
            {(['needs_data', 'pricing', 'approval', 'sent'] as QuoteState[]).map((state) => (
              <button
                type="button"
                key={state}
                onClick={() => setSelected(drafts.find((q) => q.state === state)?.id ?? selected)}
              >
                <span style={{ color: stateTone[state].fg }}>{QUOTE_STATE_LABEL[state]}</span>
                <strong>{counts[state]}</strong>
              </button>
            ))}
          </section>

          <div className="frd-grid">
            <section className="frd-list" aria-label="Quotes">
              <div className="frd-section-title">
                <span>Active quotes</span>
                <small>{drafts.length} total</small>
              </div>
              {drafts.map((q) => {
                const total = quoteTotals(q.buyAmount, q.marginPct)
                const tone = stateTone[q.state]
                return (
                  <button
                    type="button"
                    key={q.id}
                    className={selected === q.id ? 'selected' : ''}
                    onClick={() => setSelected(q.id)}
                  >
                    <div className="frd-list-top">
                      <strong>{q.customer}</strong>
                      <span style={{ color: tone.fg, background: tone.bg }}>
                        {QUOTE_STATE_LABEL[q.state]}
                      </span>
                    </div>
                    <div className="frd-route">
                      {q.origin} <i>→</i> {q.destination}
                    </div>
                    <div className="frd-list-meta">
                      <span>
                        {q.equipment}
                        {q.cargoKind === 'odc' ? ' · ODC' : ''}
                      </span>
                      <b>${total.sell.toLocaleString('en-US')}</b>
                    </div>
                  </button>
                )
              })}
            </section>

            {active && <QuotePanel quote={active} update={(change) => update(active.id, change)} />}
          </div>
        </main>
      )}

      {composer && (
        <QuoteComposer
          close={() => setComposer(false)}
          create={(q) => {
            const next = [q, ...drafts]
            save(next)
            setSelected(q.id)
            setComposer(false)
            setView('today')
          }}
        />
      )}
    </div>
  )
}

function QuotePanel({
  quote,
  update,
}: { quote: QuoteDraft; update: (change: Partial<QuoteDraft>) => void }) {
  const total = quoteTotals(quote.buyAmount, quote.marginPct)
  const blockers = quoteBlockers(quote)
  return (
    <section className="frd-panel">
      <div className="frd-panel-head">
        <div>
          <span>{quote.id}</span>
          <h2>
            {quote.origin} → {quote.destination}
          </h2>
          <p>
            {quote.customer} · Ready {quote.readyDate || 'date not set'}
          </p>
          {quote.carrier && (
            <small className="frd-commercial-line">
              {quote.carrier} · {quote.serviceMode} · {quote.incoterm} · {quote.freightTerms}
            </small>
          )}
        </div>
        <button type="button" className="frd-more" aria-label="More quote actions">
          •••
        </button>
      </div>

      {blockers.length > 0 && (
        <div className="frd-blocker">
          <b>Before this can move forward</b>
          {blockers.map((b) => (
            <span key={b}>○ {b}</span>
          ))}
        </div>
      )}

      <div className="frd-cargo">
        <div>
          <span>Cargo</span>
          <strong>{quote.cargoKind === 'odc' ? 'Out of dimension' : 'Standard FCL'}</strong>
        </div>
        <div>
          <span>Equipment</span>
          <strong>{quote.equipment}</strong>
        </div>
        <div>
          <span>Weight</span>
          <strong>{quote.weightKg ? `${quote.weightKg.toLocaleString()} kg` : '—'}</strong>
        </div>
        <div>
          <span>Dimensions</span>
          <strong>
            {quote.lengthCm
              ? `${quote.lengthCm} × ${quote.widthCm} × ${quote.heightCm} cm`
              : 'Within equipment'}
          </strong>
        </div>
      </div>

      <div className="frd-money">
        <div>
          <span>Buy cost</span>
          <label>
            ${' '}
            <input
              type="number"
              value={quote.buyAmount}
              min="0"
              onChange={(e) => update({ buyAmount: Number(e.target.value) })}
            />
          </label>
        </div>
        <div>
          <span>Target margin</span>
          <label>
            <input
              type="number"
              value={quote.marginPct}
              min="0"
              max="95"
              onChange={(e) =>
                update({
                  marginPct: Number(e.target.value),
                  state: nextQuoteState({ ...quote, marginPct: Number(e.target.value) }),
                })
              }
            />{' '}
            %
          </label>
        </div>
        <div className="frd-sell">
          <span>Customer price</span>
          <strong>${total.sell.toLocaleString('en-US')}</strong>
          <small>${total.profit.toLocaleString('en-US')} gross profit</small>
        </div>
      </div>

      <footer className="frd-panel-foot">
        <div>
          <span className="frd-dot" /> Every figure keeps its source and assumptions.
        </div>
        <button
          type="button"
          disabled={blockers.length > 0}
          onClick={() => update({ state: quote.marginPct < 12 ? 'approval' : 'sent' })}
        >
          {quote.marginPct < 12 ? 'Request approval' : 'Prepare offer'} →
        </button>
      </footer>
    </section>
  )
}

function QuoteComposer({
  close,
  create,
}: { close: () => void; create: (quote: QuoteDraft) => void }) {
  const [customer, setCustomer] = useState('')
  const [origin, setOrigin] = useState('')
  const [destination, setDestination] = useState('')
  const [equipment, setEquipment] = useState('40HC')
  const [cargoKind, setCargoKind] = useState<CargoKind>('standard')
  const [readyDate, setReadyDate] = useState('')
  const [weightKg, setWeightKg] = useState('')
  const [dims, setDims] = useState({ l: '', w: '', h: '' })
  const [cog, setCog] = useState(false)
  const [customers, setCustomers] = useState([
    'Northstar Projects',
    'Aster Machinery',
    'Hearth & Field',
    'Morrow Living',
  ])
  const [addingCustomer, setAddingCustomer] = useState(false)
  const [newCustomer, setNewCustomer] = useState('')
  const [termsOpen, setTermsOpen] = useState(false)
  const [carrier, setCarrier] = useState('Best available rate')
  const [serviceMode, setServiceMode] = useState('Port to port · FCL')
  const [incoterm, setIncoterm] = useState('FOB')
  const [freightTerms, setFreightTerms] = useState('Prepaid')
  const [quoteValidityDays, setQuoteValidityDays] = useState('7')
  const [originFreeDays, setOriginFreeDays] = useState('7')
  const [destinationFreeDays, setDestinationFreeDays] = useState('7')
  const [demurragePerDay, setDemurragePerDay] = useState('150')
  const [detentionPerDay, setDetentionPerDay] = useState('125')

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const base: QuoteDraft = {
      id: quoteId(),
      customer,
      origin: origin.toUpperCase(),
      destination: destination.toUpperCase(),
      equipment,
      cargoKind,
      readyDate,
      weightKg: weightKg ? Number(weightKg) : null,
      lengthCm: dims.l ? Number(dims.l) : null,
      widthCm: dims.w ? Number(dims.w) : null,
      heightCm: dims.h ? Number(dims.h) : null,
      centreOfGravityKnown: cog,
      carrier,
      serviceMode,
      incoterm,
      freightTerms,
      quoteValidityDays: Number(quoteValidityDays),
      originFreeDays: Number(originFreeDays),
      destinationFreeDays: Number(destinationFreeDays),
      demurragePerDay: Number(demurragePerDay),
      detentionPerDay: Number(detentionPerDay),
      buyAmount: 0,
      marginPct: 18,
      state: 'pricing',
      createdAt: new Date().toISOString(),
    }
    create({ ...base, state: nextQuoteState(base) })
  }

  return (
    <dialog open className="frd-modal" aria-label="New quote">
      <form onSubmit={submit}>
        <header>
          <div>
            <span>New quote</span>
            <h2>Start with what you know.</h2>
            <p>Missing commercial details can wait. Safety-critical ODC details cannot.</p>
          </div>
          <button type="button" onClick={close}>
            ×
          </button>
        </header>
        <div className="frd-form-grid">
          <div className="frd-customer-field">
            <label htmlFor="frd-quote-customer">Customer</label>
            <div>
              <input
                id="frd-quote-customer"
                list="frd-customer-list"
                aria-label="Quote customer"
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                placeholder="Find a customer"
                required
              />
              <datalist id="frd-customer-list">
                {customers.map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
              <button type="button" onClick={() => setAddingCustomer(!addingCustomer)}>
                ＋ New
              </button>
            </div>
            {addingCustomer && (
              <div className="frd-new-customer">
                <input
                  aria-label="New customer name"
                  value={newCustomer}
                  onChange={(e) => setNewCustomer(e.target.value)}
                  placeholder="Company name"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (!newCustomer.trim()) return
                    setCustomers([...customers, newCustomer.trim()])
                    setCustomer(newCustomer.trim())
                    setNewCustomer('')
                    setAddingCustomer(false)
                  }}
                >
                  Add
                </button>
              </div>
            )}
          </div>
          <Field label="Cargo">
            <select value={cargoKind} onChange={(e) => setCargoKind(e.target.value as CargoKind)}>
              <option value="standard">Standard container</option>
              <option value="odc">ODC / project cargo</option>
            </select>
          </Field>
          <Field label="Origin">
            <input
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
              placeholder="CNSHA"
              required
              maxLength={5}
            />
          </Field>
          <Field label="Destination">
            <input
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder="NLRTM"
              required
              maxLength={5}
            />
          </Field>
          <Field label="Equipment">
            <select value={equipment} onChange={(e) => setEquipment(e.target.value)}>
              <option>20GP</option>
              <option>40GP</option>
              <option>40HC</option>
              <option>40' FR</option>
              <option>40' OT</option>
              <option>Breakbulk</option>
            </select>
          </Field>
          <Field label="Cargo ready">
            <input type="date" value={readyDate} onChange={(e) => setReadyDate(e.target.value)} />
          </Field>
          <Field label="Weight (kg)">
            <input
              type="number"
              min="0"
              value={weightKg}
              onChange={(e) => setWeightKg(e.target.value)}
              placeholder="12000"
            />
          </Field>
          <Field label="Shipping line / rate">
            <select value={carrier} onChange={(e) => setCarrier(e.target.value)}>
              <option>Best available rate</option>
              <option>MSC · contract</option>
              <option>Maersk · contract</option>
              <option>COSCO · spot</option>
              <option>Partner / nominated agent</option>
              <option>To be confirmed</option>
            </select>
          </Field>
          <Field label="Service">
            <select value={serviceMode} onChange={(e) => setServiceMode(e.target.value)}>
              <option>Port to port · FCL</option>
              <option>Door to port · FCL</option>
              <option>Port to door · FCL</option>
              <option>Door to door · FCL</option>
              <option>LCL consolidation</option>
              <option>Project / breakbulk</option>
            </select>
          </Field>
        </div>
        <section className="frd-terms">
          <button type="button" onClick={() => setTermsOpen(!termsOpen)}>
            <span>
              <b>Terms and free time</b>
              <small>
                {incoterm} · {freightTerms} · {originFreeDays}/{destinationFreeDays} free days
              </small>
            </span>
            <i>{termsOpen ? '−' : '+'}</i>
          </button>
          {termsOpen && (
            <div className="frd-terms-grid">
              <Field label="Incoterm">
                <select value={incoterm} onChange={(e) => setIncoterm(e.target.value)}>
                  <option>EXW</option>
                  <option>FCA</option>
                  <option>FOB</option>
                  <option>CFR</option>
                  <option>CIF</option>
                  <option>DAP</option>
                  <option>DDP</option>
                </select>
              </Field>
              <Field label="Freight terms">
                <select value={freightTerms} onChange={(e) => setFreightTerms(e.target.value)}>
                  <option>Prepaid</option>
                  <option>Collect</option>
                  <option>Third party</option>
                </select>
              </Field>
              <Field label="Quote validity (days)">
                <input
                  type="number"
                  min="1"
                  value={quoteValidityDays}
                  onChange={(e) => setQuoteValidityDays(e.target.value)}
                />
              </Field>
              <Field label="Origin free time">
                <div className="frd-days">
                  <input
                    type="number"
                    min="0"
                    value={originFreeDays}
                    onChange={(e) => setOriginFreeDays(e.target.value)}
                  />
                  <span>days</span>
                </div>
              </Field>
              <Field label="Destination free time">
                <div className="frd-days">
                  <input
                    type="number"
                    min="0"
                    value={destinationFreeDays}
                    onChange={(e) => setDestinationFreeDays(e.target.value)}
                  />
                  <span>days</span>
                </div>
              </Field>
              <Field label="Demurrage / day">
                <div className="frd-days">
                  <span>$</span>
                  <input
                    type="number"
                    min="0"
                    value={demurragePerDay}
                    onChange={(e) => setDemurragePerDay(e.target.value)}
                  />
                </div>
              </Field>
              <Field label="Detention / day">
                <div className="frd-days">
                  <span>$</span>
                  <input
                    type="number"
                    min="0"
                    value={detentionPerDay}
                    onChange={(e) => setDetentionPerDay(e.target.value)}
                  />
                </div>
              </Field>
            </div>
          )}
        </section>
        {cargoKind === 'odc' && (
          <div className="frd-odc">
            <div>
              <b>ODC essentials</b>
              <span>Required before engineering review</span>
            </div>
            <div className="frd-dims">
              <Field label="Length cm">
                <input
                  type="number"
                  value={dims.l}
                  onChange={(e) => setDims({ ...dims, l: e.target.value })}
                />
              </Field>
              <Field label="Width cm">
                <input
                  type="number"
                  value={dims.w}
                  onChange={(e) => setDims({ ...dims, w: e.target.value })}
                />
              </Field>
              <Field label="Height cm">
                <input
                  type="number"
                  value={dims.h}
                  onChange={(e) => setDims({ ...dims, h: e.target.value })}
                />
              </Field>
            </div>
            <label className="frd-check">
              <input type="checkbox" checked={cog} onChange={(e) => setCog(e.target.checked)} />{' '}
              Centre of gravity is documented
            </label>
          </div>
        )}
        <footer>
          <button type="button" onClick={close}>
            Cancel
          </button>
          <button className="frd-primary" type="submit">
            Create quote →
          </button>
        </footer>
      </form>
    </dialog>
  )
}

const styles = `
.frd{--frd-accent:rgb(var(--signal-rgb,192 57 43));--surface-0:rgb(var(--surface-0-rgb,255 255 255));--surface-1:rgb(var(--surface-1-rgb,238 241 244));--surface-2:rgb(var(--surface-2-rgb,226 231 236));--text-primary:rgb(var(--ink-rgb,11 12 14));--text-muted:rgb(var(--muted-rgb,82 94 107));--border:rgb(var(--border-rgb,14 20 27)/.12);--shadow-card:var(--shadow,0 1px 2px rgb(20 32 48/.05),0 16px 34px -18px rgb(20 32 48/.22));--font-body:Switzer,ui-sans-serif,system-ui,sans-serif;--font-display:Ranade,Georgia,serif;--mono:'JetBrains Mono',ui-monospace,'SF Mono',Menlo,monospace;max-width:1180px;margin:0 auto;padding:38px 36px 72px;color:var(--text-primary);font-family:var(--font-body)}
.frd *{box-sizing:border-box}.frd button,.frd input,.frd select{font:inherit}.frd button{cursor:pointer}.frd-head{display:flex;align-items:flex-end;justify-content:space-between;gap:28px}.frd-kicker{font-family:${mono};font-size:10px;letter-spacing:.15em;text-transform:uppercase;color:var(--frd-accent);font-weight:700}.frd-kicker span{display:inline-block;width:7px;height:7px;border-radius:50%;background:var(--frd-accent);margin-right:7px}.frd h1{font-family:var(--font-display,Ranade,Georgia,serif);font-size:36px;letter-spacing:-.035em;line-height:1.05;margin:9px 0 6px;font-weight:600}.frd-head p{margin:0;color:var(--text-muted,#788392);font-size:14px}.frd-primary{border:0;border-radius:11px;background:var(--frd-accent);color:white;padding:11px 17px;font-weight:700;box-shadow:0 10px 24px -12px var(--frd-accent)}
.frd-tabs{display:flex;gap:24px;border-bottom:1px solid var(--border,#e7e7e2);margin-top:30px}.frd-tabs button{border:0;background:none;color:var(--text-muted,#788392);padding:12px 1px;position:relative;font-size:13px}.frd-tabs button.active{color:var(--text-primary,#111);font-weight:650}.frd-tabs button.active:after{content:'';position:absolute;left:0;right:0;bottom:-1px;height:2px;background:var(--frd-accent)}.frd-tabs b{font-family:${mono};font-size:10px;padding:2px 5px;border-radius:10px;background:var(--surface-1,#f3f3f0);margin-left:5px}.frd-body{padding-top:20px}.frd-states{display:grid;grid-template-columns:repeat(4,1fr);gap:10px}.frd-states button{display:flex;justify-content:space-between;align-items:center;background:var(--surface-0,#fff);border:1px solid var(--border,#e7e7e2);border-radius:12px;padding:13px 15px;text-align:left;color:var(--text-primary,#111)}.frd-states span{font-size:12px;font-weight:650}.frd-states strong{font-family:${mono};font-size:18px}.frd-grid{display:grid;grid-template-columns:330px minmax(0,1fr);gap:14px;margin-top:14px;align-items:start}.frd-list,.frd-panel{background:var(--surface-0,#fff);border:1px solid var(--border,#e7e7e2);border-radius:16px;box-shadow:var(--shadow-card,0 8px 24px -20px #0008);overflow:hidden}.frd-section-title{padding:14px 15px;display:flex;justify-content:space-between;border-bottom:1px solid var(--border,#e7e7e2);font-size:12px;font-weight:700}.frd-section-title small{color:var(--text-muted,#788392);font-weight:500}.frd-list>button{display:block;width:100%;text-align:left;border:0;border-bottom:1px solid var(--border,#e7e7e2);padding:14px 15px;background:transparent;color:inherit}.frd-list>button:hover,.frd-list>button.selected{background:var(--surface-1,#f7f7f4)}.frd-list>button.selected{box-shadow:inset 3px 0 var(--frd-accent)}.frd-list-top{display:flex;align-items:center;justify-content:space-between;gap:8px}.frd-list-top strong{font-size:13.5px}.frd-list-top span{font-size:9px;text-transform:uppercase;letter-spacing:.07em;font-weight:750;padding:4px 6px;border-radius:6px}.frd-route{font-family:${mono};font-size:12px;margin-top:8px}.frd-route i{font-style:normal;color:var(--frd-accent);padding:0 4px}.frd-list-meta{display:flex;justify-content:space-between;color:var(--text-muted,#788392);font-size:11px;margin-top:7px}.frd-list-meta b{font-family:${mono};color:var(--text-primary,#111)}
.frd-panel-head{display:flex;justify-content:space-between;padding:22px 24px;border-bottom:1px solid var(--border,#e7e7e2)}.frd-panel-head span{font-family:${mono};font-size:10px;color:var(--text-muted,#788392)}.frd-panel-head h2{font-family:var(--font-display,Ranade,Georgia,serif);font-size:25px;margin:5px 0 3px;letter-spacing:-.025em}.frd-panel-head p{margin:0;color:var(--text-muted,#788392);font-size:12px}.frd-more{border:0;background:none;color:var(--text-muted);letter-spacing:2px}.frd-blocker{margin:16px 22px 0;border:1px solid #f59e0b55;background:#f59e0b0c;border-radius:11px;padding:12px 14px;display:grid;gap:5px;font-size:12px;color:#a16207}.frd-blocker b{margin-bottom:2px}.frd-cargo{display:grid;grid-template-columns:repeat(4,1fr);gap:1px;background:var(--border,#e7e7e2);border-block:1px solid var(--border,#e7e7e2);margin-top:18px}.frd-cargo>div{background:var(--surface-0,#fff);padding:14px}.frd-cargo span,.frd-money span{display:block;font-family:${mono};font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:var(--text-muted,#788392);margin-bottom:6px}.frd-cargo strong{font-size:12px}.frd-money{display:grid;grid-template-columns:1fr 1fr 1.25fr;padding:22px;gap:12px}.frd-money>div{border:1px solid var(--border,#e7e7e2);border-radius:12px;padding:13px}.frd-money label{display:flex;align-items:center;gap:5px;font-family:${mono};font-weight:700}.frd-money input{width:100%;min-width:0;border:0;outline:0;background:transparent;color:inherit;font-family:${mono};font-weight:700}.frd-sell{background:color-mix(in srgb,var(--frd-accent) 7%,var(--surface-0,#fff));border-color:color-mix(in srgb,var(--frd-accent) 32%,transparent)!important}.frd-sell strong{font-family:${mono};font-size:24px;display:block}.frd-sell small{color:var(--text-muted,#788392);font-size:11px}.frd-panel-foot{display:flex;justify-content:space-between;align-items:center;gap:16px;padding:16px 22px;border-top:1px solid var(--border,#e7e7e2);font-size:11px;color:var(--text-muted,#788392)}.frd-dot{display:inline-block;width:6px;height:6px;border-radius:50%;background:#10b981;margin-right:5px}.frd-panel-foot button{border:0;background:var(--text-primary,#111);color:var(--surface-0,#fff);border-radius:9px;padding:9px 13px;font-weight:700}.frd-panel-foot button:disabled{opacity:.35;cursor:not-allowed}
.frd-modal{position:fixed;inset:0;z-index:1000;width:100%;max-width:none;height:100%;max-height:none;margin:0;padding:0;border:0;color:inherit;background:#05070bac;backdrop-filter:blur(8px);display:flex;justify-content:flex-end}.frd-modal form{width:min(620px,100%);height:100%;overflow:auto;background:var(--surface-0,#fff);padding:30px;box-shadow:-20px 0 70px #0004}.frd-modal header{display:flex;justify-content:space-between;gap:20px}.frd-modal header>button{border:0;background:var(--surface-1,#eee);width:34px;height:34px;border-radius:50%;font-size:20px;color:inherit}.frd-modal header>div>span{font-family:${mono};font-size:10px;text-transform:uppercase;letter-spacing:.14em;color:var(--frd-accent);font-weight:700}.frd-modal h2{font-family:var(--font-display,Ranade,Georgia,serif);font-size:30px;letter-spacing:-.03em;margin:7px 0 4px}.frd-modal header p{margin:0;color:var(--text-muted,#788392);font-size:13px}.frd-form-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:26px}.frd-field{display:grid;gap:6px}.frd-field>label{font-family:${mono};font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:var(--text-muted,#788392)}.frd-field input,.frd-field select{width:100%;border:1px solid var(--border,#e7e7e2);border-radius:10px;background:var(--surface-0,#fff);color:inherit;padding:11px 12px;outline:none}.frd-field input:focus,.frd-field select:focus{border-color:var(--frd-accent);box-shadow:0 0 0 3px color-mix(in srgb,var(--frd-accent) 12%,transparent)}.frd-odc{margin-top:18px;border:1px solid color-mix(in srgb,var(--frd-accent) 30%,transparent);background:color-mix(in srgb,var(--frd-accent) 5%,var(--surface-0,#fff));border-radius:13px;padding:15px}.frd-odc>div:first-child{display:flex;justify-content:space-between;font-size:12px}.frd-odc>div:first-child span{color:var(--text-muted,#788392)}.frd-dims{display:grid;grid-template-columns:repeat(3,1fr);gap:9px;margin-top:13px}.frd-check{display:flex;align-items:center;gap:8px;font-size:12px;margin-top:13px}.frd-modal footer{display:flex;justify-content:flex-end;gap:9px;margin-top:26px;padding-top:18px;border-top:1px solid var(--border,#e7e7e2)}.frd-modal footer button{border:1px solid var(--border,#e7e7e2);background:var(--surface-0,#fff);color:inherit;border-radius:10px;padding:10px 15px;font-weight:650}.frd-modal footer .frd-primary{background:var(--frd-accent);color:#fff;border-color:var(--frd-accent)}
.frd-commercial-line{display:block;color:var(--text-muted);font-size:9px;margin-top:5px}.frd-customer-field{display:grid;gap:6px}.frd-customer-field>label{font-family:${mono};font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:var(--text-muted,#788392)}.frd-customer-field>div{display:grid;grid-template-columns:1fr auto}.frd-customer-field select,.frd-customer-field input{width:100%;border:1px solid var(--border,#e7e7e2);background:var(--surface-0);color:inherit;padding:11px 12px}.frd-customer-field select{border-radius:10px 0 0 10px}.frd-customer-field>div>button,.frd-new-customer button{border:1px solid var(--border);border-left:0;background:var(--surface-1);color:var(--frd-accent);border-radius:0 10px 10px 0;padding:0 10px;font-size:9px;font-weight:700}.frd-customer-field .frd-new-customer{display:grid;grid-template-columns:1fr auto}.frd-new-customer input{border-radius:8px 0 0 8px;padding:8px 10px;font-size:10px}.frd-terms{margin-top:16px;border:1px solid var(--border);border-radius:12px;overflow:hidden}.frd-terms>button{display:flex;justify-content:space-between;align-items:center;width:100%;border:0;background:var(--surface-1);color:inherit;text-align:left;padding:11px 13px}.frd-terms>button b,.frd-terms>button small{display:block}.frd-terms>button b{font-size:11px}.frd-terms>button small{font-size:8.5px;color:var(--text-muted);margin-top:2px}.frd-terms>button i{font-style:normal;color:var(--frd-accent);font-size:16px}.frd-terms-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;padding:13px}.frd-days{position:relative;display:flex;align-items:center;border:1px solid var(--border);border-radius:10px;background:var(--surface-0)}.frd-days input{border:0!important;box-shadow:none!important;min-width:0}.frd-days span{font-size:9px;color:var(--text-muted);padding:0 9px}.frd-days>span:first-child{padding-right:0}.frd-days>span:first-child+input{padding-left:5px}
@media(max-width:760px){.frd{padding:24px 16px 60px}.frd-head{align-items:flex-start;flex-direction:column}.frd-head .frd-primary{width:100%}.frd h1{font-size:30px}.frd-states{grid-template-columns:1fr 1fr}.frd-grid{grid-template-columns:1fr}.frd-list{max-height:310px;overflow:auto}.frd-cargo{grid-template-columns:1fr 1fr}.frd-money{grid-template-columns:1fr}.frd-panel-foot{align-items:flex-start;flex-direction:column}.frd-panel-foot button{width:100%}.frd-form-grid{grid-template-columns:1fr}.frd-terms-grid{grid-template-columns:1fr 1fr}.frd-dims{grid-template-columns:1fr 1fr 1fr}.frd-modal form{padding:22px 18px}}
`
